import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import { randomRoller } from '@dcc/rules/rules/dice.ts';
import type { SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { SheetsSaveError } from '../obr/sheetsApiStorage.ts';
import type { DicePlusRequester } from './diceRolls.ts';
import { obtainNotationDice, recordingRoller } from './diceRolls.ts';
import { planPartyTimeEvent } from './partyTime.ts';

export interface PartyTimeSyncResult {
  applied: number;
  notes: string[];
  blocked: string[];
  deferred: boolean;
  /** True when the shared storage snapshot was reloaded while holding the coordination gate. */
  rebased: boolean;
}

export type DicePlusRequesterFactory = () => Promise<DicePlusRequester>;

/**
 * Pulls and applies every pending GM party-time event in order.
 *
 * We fetch documents only when a party event exists, the GM room state changed,
 * or a realtime sync nudge arrives. The ordinary 5 s loop checks only the small
 * event list and room-state version before deciding whether a full rebase is needed. Each apply is version checked and atomically marks
 * the event receipt on the server. A second browser that loses the race treats
 * "already applied" as success after refreshing its snapshot.
 */
export async function syncPartyTimeEvents(
  storage: SheetsApiStorage,
  services: AppServices,
  coordinationGate: SheetMutationGate,
  getDicePlus: DicePlusRequesterFactory = async () => null,
  forceRebase = false,
  prepareCatalogue?: () => Promise<void>,
): Promise<PartyTimeSyncResult> {
  const events = await storage.listPartyEvents();
  const roomStateChanged = await storage.checkRoomState();
  if (events.length === 0 && !forceRebase && !roomStateChanged) {
    return { applied: 0, notes: [], blocked: [], deferred: false, rebased: false };
  }

  // A Dice+ roll can spend seconds between reading its Character and saving the
  // result. Never refresh the shared storage cache or apply a GM event inside
  // that window: doing so could let the old computation save over the rest.
  // The event stays pending and the next poll retries.
  const gateProblem = coordinationGate.claim();
  if (gateProblem !== null) return { applied: 0, notes: [], blocked: [], deferred: true, rebased: false };

  try {
    // The event may have been posted after our current sheet snapshot, and the GM
    // may also have changed AI Favor. Rebase only after we own the same lock as
    // every player play mutation.
    await storage.load();
    // A GM award/edit and a time event can arrive in the same poll. Install
    // the current room definitions under the mutation lock BEFORE applying
    // recovery or round regeneration to a crawler wearing that equipment.
    // If the catalogue cannot be fetched, leave the event pending for retry.
    await prepareCatalogue?.();

    // A GM save (AI Favor now, full sheet editing later) may have no party event.
    // A broadcast can still force an immediate safe rebase through this same
    // mutation gate instead of waiting for a stale-save conflict to reveal it.
    if (events.length === 0) {
      return { applied: 0, notes: [], blocked: [], deferred: false, rebased: true };
    }

    const notes: string[] = [];
    const blocked: string[] = [];
    let applied = 0;

    for (const event of events) {
      const character = await services.repository.load(event.characterId);
      const session = await services.sessions.loadOrStart(event.characterId);
      const runtime = await services.runtime.loadOrStart(event.characterId);
      const now = new Date().toISOString();

      let roller = randomRoller;
      let diceNote: string | null = null;

      // Advancement rolls are real table-facing rolls just like Checks. Dry-run
      // the engine to learn how many d20s it needs, then let Dice+ provide those
      // faces. If Dice+ is absent, the same engine rolls locally.
      if (event.kind === 'pass-two-hours' || event.kind === 'end-of-floor') {
        const recording = recordingRoller();
        const dry = planPartyTimeEvent(character, session, runtime, event.kind, recording.roller, now);
        if (!dry.ok) {
          blocked.push(dry.reason);
          continue;
        }
        if (recording.sides.length > 0) {
          const count = recording.sides.length;
          const request = await getDicePlus();
          const notation = `${count}d20 #Skill advancement${count === 1 ? '' : ' checks'}`;
          const dice = await obtainNotationDice(notation, recording.sides, request);
          if (!dice.ok) {
            blocked.push(dice.reason);
            continue;
          }
          roller = dice.roller;
          diceNote = dice.note;
        }
      }

      const plan = planPartyTimeEvent(character, session, runtime, event.kind, roller, now);
      if (!plan.ok) {
        blocked.push(plan.reason);
        continue;
      }

      try {
        await storage.applyPartyEvent(event.eventId, event.characterId, {
          ...(plan.character ? { character: plan.character } : {}),
          ...(plan.session ? { session: plan.session } : {}),
          runtime: plan.runtime,
        });
        applied += 1;
        notes.push(diceNote ? `${plan.note} ${diceNote}` : plan.note);
      } catch (error) {
        if (error instanceof SheetsSaveError && (error.reason === 'event-applied' || error.reason === 'event-cancelled')) {
          // Another open copy won, or the snapshotted crawler was deleted.
          // Rebase and treat the target as settled rather than inviting a retry.
          await storage.load();
          continue;
        }
        throw error;
      }
    }

    return { applied, notes, blocked, deferred: false, rebased: true };
  } finally {
    coordinationGate.release();
  }
}
